import { useEffect, useState } from "react";
import { Lock, ArrowRight, ShieldCheck } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { billingCheckout, trackEvent } from "@/lib/api";
import { detectBillingCurrency } from "@/lib/billing";

/**
 * One-shot "trial ended" modal.
 *
 * Fires the first time the user opens a project whose trial has expired.
 * Subsequent visits hide it (localStorage flag keyed by project id) so we
 * don't annoy them — the persistent in-page `TrialBanner` continues to
 * surface the unlock CTA. Clicking "Unlock" kicks off Paystack checkout
 * for the Single Project plan; backend webhook then auto-promotes the
 * trial project on success.
 */
export default function TrialExpiredModal({ project }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isExpiredTrial =
    Boolean(project?.trial) &&
    Boolean(project?.trial_expires_at) &&
    new Date(project.trial_expires_at).getTime() <= Date.now();
  const seenKey = project?.id ? `fc_trial_expired_seen_${project.id}` : null;

  useEffect(() => {
    if (!isExpiredTrial || !seenKey) return;
    let seen = false;
    try {
      seen = !!window.localStorage.getItem(seenKey);
    } catch {
      seen = false;
    }
    if (seen) return;
    setOpen(true);
    // Fire-and-forget — capture the impression for the funnel.
    trackEvent("trial_expired", `project:${project?.id}`, { project_id: project?.id });
  }, [isExpiredTrial, seenKey, project?.id]);

  const close = () => {
    setOpen(false);
    if (seenKey) {
      try { window.localStorage.setItem(seenKey, "1"); } catch { /* ignore */ }
    }
  };

  const price = detectBillingCurrency() === "ZAR" ? "R280" : "$19";

  const startCheckout = async () => {
    trackEvent("trial_unlock_clicked", "modal:trial_expired", { project_id: project?.id });
    setBusy(true);
    try {
      const callback_url = `${window.location.origin}/billing/callback`;
      const { authorization_url } = await billingCheckout({
        plan: "single",
        currency: detectBillingCurrency(),
        callback_url,
      });
      window.location.href = authorization_url;
    } catch (err) {
      if (err?.response?.status === 503) {
        toast.error("Checkout isn't live in this environment yet.");
      } else {
        toast.error("Could not start checkout — please try again.");
      }
      setBusy(false);
    }
  };

  if (!isExpiredTrial) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogContent
        className="bg-white max-w-md p-0 overflow-hidden"
        data-testid="trial-expired-modal"
      >
        {/* Header strip */}
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-b border-amber-200 px-6 pt-6 pb-5">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-medium bg-amber-100 border border-amber-200 text-amber-900 rounded-full px-2.5 py-1">
            <Lock className="w-3 h-3" /> Trial ended
          </div>
          <DialogHeader className="mt-3">
            <DialogTitle className="fc-heading text-[20px] sm:text-[22px] text-[#1C1C1C] leading-snug">
              Your 7-day FlipCost trial has ended.
            </DialogTitle>
          </DialogHeader>
          <p className="text-[13.5px] text-[#57534E] mt-2 leading-relaxed">
            Your project is safe — it's now read-only. Unlock the full FlipCost project for{" "}
            <span className="fc-mono text-[#1C1C1C]">{price}</span> to keep editing and add the rest of the property.
          </p>
        </div>

        {/* Value bullets */}
        <div className="px-6 py-5 space-y-2.5">
          {[
            "Add every room (no 1-room cap)",
            "Plan extensions, exterior, systems & soft costs",
            "Track Estimate vs Actual spend",
            "One-off payment — buy more whenever you start a new deal",
          ].map((b) => (
            <div key={b} className="flex items-start gap-2 text-[13.5px] text-[#1C1C1C]">
              <ShieldCheck className="w-4 h-4 text-sage-700 mt-0.5 shrink-0" />
              <span>{b}</span>
            </div>
          ))}
        </div>

        {/* Footer CTAs */}
        <div className="border-t border-black/[0.06] px-6 py-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={close}
            data-testid="trial-expired-modal-later"
            className="text-sm text-[#78716C] hover:text-[#1C1C1C] transition-colors"
          >
            Maybe later
          </button>
          <button
            type="button"
            onClick={startCheckout}
            disabled={busy}
            data-testid="trial-expired-modal-unlock"
            className="inline-flex items-center gap-1.5 bg-sage-700 hover:bg-sage-800 text-white text-sm font-medium rounded-md h-10 px-4 transition-colors disabled:opacity-60"
          >
            {busy ? "Starting…" : `Unlock for ${price}`}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
